import { COLOR } from '../../../design-tokens'
import { imagesPaths } from 'app/common/utils'
import Supercluster from 'supercluster'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { ALL_ZOOM, CURRENT_MAP_VIEW } from './MapWoosmap'
import type { RestaurantMarkerDTO } from 'shared/types'
import { markersBase64 } from './markersBase64'

type StoreHours = {
    end: string,
    start: string
}

type Geometry = {
    type: string,
    coordinates: Array<number>
}

type ClusterFeature = {
    geometry: Geometry,
    id: number,
    properties: {
        cluster: boolean,
        cluster_id: number,
        point_count: number,
        point_count_abbreviated: number
    },
    type: string
}

type MarkerFeature = {
    geometry: Geometry,
    properties: {
        address: {
            city: string,
            country_code: string,
            lines: Array<string>,
            zipcode: string
        },
        contact: {
            email: string,
            phone: string
        },
        last_updated: string,
        name: string,
        open: {
            current_slice: StoreHours,
            open_hours: Array<StoreHours>,
            open_now: boolean,
            week_day: number
        },
        opening_hours: {
            timezone: string,
            usual: {
                [number]: Array<StoreHours>
            }
        },
        store_id: string,
        tags: Array<any>,
        types: Array<string>,
        user_properties: {},
        weekly_opening: {
            [number]: { hours: Array<StoreHours>, isSpecial: boolean },
            timezone: string
        }
    },
    type: string
}

//$FlowFixMe
type createClusterIconProps = Pick<
    createClustersProps,
    'activeRestaurantId',
    'map',
    'onStoreSelected'
> & {
    feature: ClusterFeature | MarkerFeature
}

const createClusterIcon = ({
    activeRestaurantId,
    feature,
    map,
    onStoreSelected
}: createClusterIconProps) => {
    const isCluster =
        !!feature.properties.cluster && feature.properties.cluster === true

    const isActive =
        !!feature.properties.store_id &&
        feature.properties.store_id === activeRestaurantId

    const marker = new window.woosmap.map.Marker({
        label: {
            text: !!feature.properties.point_count_abbreviated
                ? feature.properties.point_count_abbreviated
                : undefined,
            color: COLOR.SECONDARY,
            fontSize: '18px'
        },
        position: {
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0]
        },
        icon: {
            labelOrigin: new window.woosmap.map.Point(24, 24),
            url: isCluster
                ? markersBase64.markerCluster
                : isActive
                ? markersBase64.markerActive
                : markersBase64.marker
        }
    })
    marker.setMap(map)
    marker.addListener('click', () => {
        if (isCluster) {
            map.setZoom(map.getZoom() + 1)
            map.setCenter({
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0]
            })
        } else {
            onStoreSelected(feature)
        }
    })
    return marker
}

//$FlowFixMe
type createClustersProps = Omit<
    useDisplayClustersProps,
    'isMapLoaded',
    'currentView'
> & {
    activeRestaurantId: string,
    markers: [],
    markersAPI: RestaurantMarkerDTO[],
    setMarkers: ([]) => void
}

const createClusters = ({
    activeRestaurantId,
    map,
    markers,
    markersAPI,
    zoomAndBbox,
    onStoreSelected,
    setMarkers
}: createClustersProps) => {
    if (map && markersAPI && markersAPI.length > 0) {
        const filteredRestaurants = markersAPI.map(({ lng, lat, id }) => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lng, lat]
            },
            id: id,
            properties: { store_id: id }
        }))

        markers.map(marker => {
            marker.setMap(null)
        })
        setMarkers(markers)

        const index = new Supercluster({
            radius: 100,
            maxZoom: 16,
            minZoom: ALL_ZOOM // prevents a single cluster from being displayed with the total number of restaurants -> BE-7941
        }).load(filteredRestaurants)
        const clusters = index.getClusters(zoomAndBbox.bbox, zoomAndBbox.zoom)
        setMarkers(
            clusters.map(feature => {
                return createClusterIcon({
                    activeRestaurantId,
                    feature,
                    map,
                    onStoreSelected
                })
            })
        )
    }
}

type useDisplayClustersProps = {
    currentView: string,
    isMapLoaded: boolean,
    map: any,
    zoomAndBbox: { zoom: number, bbox: [number] },
    onStoreSelected: (store: any) => void
}

export const useDisplayClusters = ({
    currentView,
    isMapLoaded,
    map,
    zoomAndBbox,
    onStoreSelected
}: useDisplayClustersProps) => {
    const { activeRestaurant, markers: markersApi, restaurants } = useSelector(
        (state: RootState) => state.storeLocatorPageState
    )

    const filteredMarkers = restaurants
        ? restaurants &&
          restaurants
              .filter(restaurant => restaurant.statusType !== 'PRE_OPEN')
              .map(({ id, lat, lng }) => ({
                  id,
                  lat,
                  lng
              }))
        : markersApi
    const [markers, setMarkers] = useState([])

    useEffect(() => {
        if (map && currentView !== CURRENT_MAP_VIEW.RESTAURANT) {
            createClusters({
                activeRestaurantId: activeRestaurant?.id ?? '',
                map,
                markers,
                markersAPI: filteredMarkers,
                zoomAndBbox,
                onStoreSelected,
                setMarkers
            })
        }
    }, [
        map,
        activeRestaurant,
        restaurants,
        zoomAndBbox,
        isMapLoaded,
        currentView
    ])
}
